import React, { useState, useEffect, useContext, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Layout from './components/layout/Layout';
import LoginScreen from './components/LoginScreen';
import SessionEndedScreen from './components/SessionEndedScreen';
import Dashboard from './components/Dashboard';
import CreateTaskScreen from './components/CreateTaskScreen';
import UpdateTaskScreen from './components/UpdateTaskScreen';
import AllTasksScreen from './components/AllTasksScreen';
import EmployeeListScreen from './components/EmployeeListScreen';
import ConfigScreen from './components/ConfigScreen';
import TicketAgeingReportScreen from './components/TicketAgeingReportScreen';
import NotificationPollingBootstrap from './components/layout/NotificationPollingBootstrap';

import { AuthContext } from './auth/AuthContext';
import {
  registerSessionExpiredNavigator,
  unregisterSessionExpiredNavigator,
} from './utils/sessionExpiredNavigation';

/** Stable component identity — do not define inside App or every parent re-render remounts all children. */
function ProtectedRoute({ children, loading }) {
  if (loading) {
    return <div>Loading...</div>;
  }
  const token = localStorage.getItem('access_token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

/** Stable layout wrapper — must stay outside App for the same reason as ProtectedRoute. */
function LayoutWithUserData({ children, userName }) {
  return (
    <Layout userName={userName}>
      {children}
    </Layout>
  );
}

function App() {
  const [userData, setUserData] = useState({
    name: '',
    id: ''
  });

  const { user, loginWithCode, loading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const oauthCallbackHandledRef = useRef(false);

  // Handle code parameter for OAuth flow (Azure AD)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');
    const state = queryParams.get('state');

    if (!code) {
      // Allow a later OAuth round-trip (ref was set true on previous callback completion)
      oauthCallbackHandledRef.current = false;
      return;
    }

    // One callback per authorization code (prevents duplicate exchange if effect re-runs)
    const lockKey = `oauth_code_lock_${code}`;
    if (sessionStorage.getItem(lockKey) || oauthCallbackHandledRef.current) {
      return;
    }
    sessionStorage.setItem(lockKey, '1');
    oauthCallbackHandledRef.current = true;

    loginWithCode(code, state)
      .then(() => {
        sessionStorage.removeItem(lockKey);
        navigate('/', { replace: true });
      })
      .catch((err) => {
        console.error('Login failed:', err);
        sessionStorage.removeItem(lockKey);
        oauthCallbackHandledRef.current = false;
        navigate('/login', { replace: true });
      });
  }, [location.search, loginWithCode, navigate]);

  // Let axios (and other non-React code) navigate to the global session-ended screen
  useEffect(() => {
    registerSessionExpiredNavigator(navigate);
    return () => unregisterSessionExpiredNavigator();
  }, [navigate]);

  // Update userData when user context changes
  useEffect(() => {
    if (user) {
      setUserData({
        name: user.userName || '',
        id: user.userId || ''
      });
    } else if (!loading && location.pathname !== '/login' && location.pathname !== '/session-ended') {
      // Only navigate to login if we're not on the login page and not loading
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
      }
    }
  }, [user, loading, navigate, location.pathname]);

  return (
    <div className="App">
      <NotificationPollingBootstrap />
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <Dashboard userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <Dashboard userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-task"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <CreateTaskScreen userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-task/:taskId"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <UpdateTaskScreen userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route
          path="/all-tasks"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <AllTasksScreen userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <EmployeeListScreen userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <ConfigScreen userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-ageing-report"
          element={
            <ProtectedRoute loading={loading}>
              <LayoutWithUserData userName={userData.name}>
                <TicketAgeingReportScreen userData={userData} />
              </LayoutWithUserData>
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/session-ended" element={<SessionEndedScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;